import { useId, type ReactNode } from 'react';

/**
 * One block of a screen: a titled card with a hairline between its name and its contents.
 *
 * Restyled 22 August 2026 on TailAdmin's `ComponentCard` (MIT — `shared/tailadmin/LICENSE.md`).
 * Three changes, and each is the reason the panel read as a wireframe rather than a product:
 *
 * ⚠️ **`rounded-2xl`, not `rounded-lg`.** A 6px corner on a card 900px wide is a corner nobody
 * perceives as a corner — the eye reads a rectangle with a slightly soft edge, which is what a
 * placeholder looks like.
 *
 * ⚠️ **The title is a title.** It was 12px uppercase in muted grey — the treatment for a field
 * label, applied to the name of a section. Seven of them stacked on the mission screen gave that
 * page no hierarchy at all: every heading was quieter than the data under it.
 *
 * ⚠️ **A shadow, so a card sits on the page rather than being drawn on it.** The values are ours,
 * not the template's; see the note beside `--shadow-theme-*` in `app/styles.css` for why copying
 * TailAdmin's would have produced no visible elevation on this ground.
 *
 * ⚠️ A ruled header, not a floating label. §23.1's mission screen stacks seven of these, and on a
 * stack that long a title with only whitespace under it reads as a caption for whatever happens to
 * sit above — an operator scanning for "الأعضاء" should find where it begins, not guess.
 */
export function Panel({
  title,
  description,
  actions,
  children,
  bodyClassName = 'flex flex-col gap-4 p-4 sm:p-6',
}: {
  title: string;

  /** One sentence on what this section is for, when the title alone leaves it ambiguous. */
  description?: string;

  /** Controls that act on this section alone. */
  actions?: ReactNode;

  children: ReactNode;

  /**
   * ⚠️ For the one case the padding is wrong: a table that has to reach the card's own edges.
   * Passing `p-0` there beats every list re-inventing the card to avoid a double gutter.
   */
  bodyClassName?: string;
}) {
  const headingId = useId();

  return (
    // ⚠️ Labelled by its own heading, which makes it a landmark rather than an anonymous box.
    // §23.1 stacks seven of these on the mission screen: without a name, a screen-reader user
    // arrives at "الأعضاء" as loose text somewhere in one long document and has no way to jump
    // between sections.
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm"
    >
      {/* ⚠️ **`py-4`, and `min-h` on the row rather than more padding.**
          A title-only header at `py-5` with a 1.5rem heading is a 68px band holding one short word,
          and on a card 1100px wide that reads as an empty strip somebody forgot to fill — which is
          precisely how the filter card looked. The row is now the height of its content plus a
          consistent gutter, and stays that height whether or not it carries actions, so a stack of
          cards keeps one rhythm. */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={headingId} className="text-base font-semibold text-gray-800">
            {title}
          </h2>
          {description ? <p className="text-theme-sm text-gray-500">{description}</p> : null}
        </div>

        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>

      <div className={`border-t border-gray-100 ${bodyClassName}`}>{children}</div>
    </section>
  );
}
